/**
 * HighwayRadialMap
 *
 * A tiny, dependency-free radial mini-map that plots the property pin at the
 * centre + the nearest N highways as spokes radiating outward at their true
 * geographic bearing. Distance uses a light log-scale so a 4 km highway and
 * a 40 km highway are both readable on the same canvas.
 *
 * The SVG body is built as a raw markup string and injected via
 * `dangerouslySetInnerHTML` — this avoids a rare React quirk where SVG
 * children rendered inside a Radix Dialog Portal can lose their namespace
 * and appear as `<span>` (invisible) elements.
 */

import React from 'react';
import { Milestone } from 'lucide-react';

const SIZE = 320;
const CENTER = SIZE / 2;
const R_MAX = SIZE / 2 - 44;
const RINGS = [0.33, 0.66, 1.0];
const RING_LABELS = ['near', 'mid', 'far'];
const HW_COLORS = ['#059669', '#0891b2', '#d97706', '#7c3aed', '#dc2626'];

// Short display label used inside the SVG next to each spoke tip.
// Keeps text short so labels don't overlap in a 320px canvas.
const shortHighwayLabel = (name) => {
  if (!name) return '';
  // Take the part before " at " or first parenthesis for common patterns like
  // "NH-16 at Kolkata (Vidyasagar Setu)" -> "NH-16".
  const cut = name.split(/ at | \(/)[0].trim();
  if (cut.length <= 14) return cut;
  return `${cut.slice(0, 12)}…`;
};

// XML-escape a highway name for safe use inside SVG text.
const escXml = (s) =>
  String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const buildSvgBody = (items) => {
  const maxKm = Math.max(...items.map((h) => h.distance_km));
  const scale = (km) => {
    if (maxKm <= 0) return 0;
    const t = Math.log10(1 + km) / Math.log10(1 + maxKm);
    return Math.max(0.15, Math.min(1, t));
  };
  const polar = (bearingDeg, km) => {
    const r = R_MAX * scale(km);
    const theta = (bearingDeg - 90) * (Math.PI / 180);
    return {
      x: CENTER + r * Math.cos(theta),
      y: CENTER + r * Math.sin(theta),
      // Unit vector along the spoke — used to offset the labels outward.
      ux: Math.cos(theta),
      uy: Math.sin(theta),
    };
  };

  const parts = [];

  // ---- <defs>: one arrowhead marker per highway colour --------------------
  parts.push('<defs>');
  HW_COLORS.forEach((c, i) => {
    parts.push(
      `<marker id="am-arrow-${i}" viewBox="0 0 10 10" refX="9" refY="5" ` +
      `markerWidth="6" markerHeight="6" orient="auto-start-reverse">` +
      `<path d="M 0 0 L 10 5 L 0 10 z" fill="${c}"/></marker>`,
    );
  });
  parts.push('</defs>');

  // ---- concentric distance rings ------------------------------------------
  RINGS.forEach((frac) => {
    parts.push(
      `<circle cx="${CENTER}" cy="${CENTER}" r="${R_MAX * frac}" fill="none" ` +
      `stroke="#cbd5e1" stroke-width="1.2" stroke-dasharray="4 4"/>`,
    );
  });

  // ---- compass N/E/S/W ticks ---------------------------------------------
  const ticks = [
    { d: 'N', dx: 0, dy: -R_MAX - 16 },
    { d: 'E', dx: R_MAX + 16, dy: 0 },
    { d: 'S', dx: 0, dy: R_MAX + 20 },
    { d: 'W', dx: -R_MAX - 16, dy: 0 },
  ];
  ticks.forEach((t) => {
    parts.push(
      `<text x="${CENTER + t.dx}" y="${CENTER + t.dy}" text-anchor="middle" ` +
      `dominant-baseline="middle" fill="#94a3b8" font-size="11" ` +
      `font-weight="700">${t.d}</text>`,
    );
  });

  // ---- ring labels: near / mid / far -------------------------------------
  RINGS.forEach((frac, i) => {
    parts.push(
      `<text x="${CENTER}" y="${CENTER - R_MAX * frac - 3}" text-anchor="middle" ` +
      `fill="#cbd5e1" font-size="9" font-style="italic">${RING_LABELS[i]}</text>`,
    );
  });

  // ---- highway arrows: land -> highway with arrowhead, then dot ----------
  items.forEach((h, i) => {
    const p = polar(h.bearing_deg, h.distance_km);
    const c = HW_COLORS[i % HW_COLORS.length];
    // Start the arrow slightly outside the property pin so it doesn't overlap.
    const startPad = 10;
    const x1 = CENTER + p.ux * startPad;
    const y1 = CENTER + p.uy * startPad;
    // Stop the arrow a touch short of the dot to make the arrow tip visible.
    const endPad = 8;
    const x2 = p.x - p.ux * endPad;
    const y2 = p.y - p.uy * endPad;
    parts.push(
      `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" ` +
      `x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${c}" ` +
      `stroke-width="2.6" stroke-linecap="round" opacity="0.95" ` +
      `marker-end="url(#am-arrow-${i})"/>`,
    );
    // Highway endpoint dot.
    parts.push(
      `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="5.5" ` +
      `fill="${c}" stroke="#ffffff" stroke-width="1.8"/>`,
    );
  });

  // ---- highway name + distance labels ------------------------------------
  // Placed radially outward from the endpoint so they don't hide the spoke.
  items.forEach((h, i) => {
    const p = polar(h.bearing_deg, h.distance_km);
    const c = HW_COLORS[i % HW_COLORS.length];
    // Label sits ~14 px further out than the dot, in the same radial direction.
    const lx = p.x + p.ux * 14;
    const ly = p.y + p.uy * 14;
    // Pick a text anchor based on whether the label is on the left / right side.
    const anchor = p.ux > 0.25 ? 'start' : p.ux < -0.25 ? 'end' : 'middle';
    const shortName = escXml(shortHighwayLabel(h.name));
    parts.push(
      `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" text-anchor="${anchor}" ` +
      `dominant-baseline="middle" fill="${c}" font-size="11" ` +
      `font-weight="800">${shortName}</text>`,
    );
    // Small distance chip along the middle of the spoke.
    const midX = CENTER + (p.x - CENTER) * 0.55;
    const midY = CENTER + (p.y - CENTER) * 0.55;
    // Offset slightly perpendicular to the spoke so the number sits *beside*
    // the line, not on top of it.
    const perpDx = -p.uy * 10;
    const perpDy = p.ux * 10;
    const chipX = midX + perpDx;
    const chipY = midY + perpDy;
    const kmText = `${h.distance_km.toFixed(1)} km`;
    // Rough text width — 6.4 px per char for the 10 px font.
    const chipW = kmText.length * 6.4 + 10;
    parts.push(
      `<rect x="${(chipX - chipW / 2).toFixed(1)}" y="${(chipY - 8).toFixed(1)}" ` +
      `width="${chipW.toFixed(1)}" height="16" rx="8" ry="8" ` +
      `fill="#ffffff" stroke="${c}" stroke-width="1"/>`,
    );
    parts.push(
      `<text x="${chipX.toFixed(1)}" y="${(chipY + 0.5).toFixed(1)}" ` +
      `text-anchor="middle" dominant-baseline="middle" fill="${c}" ` +
      `font-size="10" font-weight="700">${kmText}</text>`,
    );
  });

  // ---- property pin (drawn LAST so it sits above everything) --------------
  parts.push(
    `<circle cx="${CENTER}" cy="${CENTER}" r="9" fill="#0f172a" ` +
    `stroke="#facc15" stroke-width="2.2"/>`,
    `<circle cx="${CENTER}" cy="${CENTER}" r="3" fill="#facc15"/>`,
    `<text x="${CENTER}" y="${CENTER + 24}" text-anchor="middle" ` +
    `fill="#0f172a" font-size="10" font-weight="800">Your plot</text>`,
  );

  return parts.join('');
};

const HighwayRadialMap = ({ highways = [], propertyLabel = 'This plot' }) => {
  const items = highways.filter(
    (h) => typeof h?.distance_km === 'number' && typeof h?.bearing_deg === 'number',
  );
  if (items.length === 0) return null;

  const svgBody = buildSvgBody(items);
  // Wrap in a full SVG document string with explicit namespace — parsed via
  // dangerouslySetInnerHTML so the browser handles namespaces itself.
  const svgMarkup = `<svg width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}" xmlns="http://www.w3.org/2000/svg" aria-label="Radial map showing nearby highway distances and bearings">${svgBody}</svg>`;

  return (
    <div className="mt-3 rounded-xl border border-emerald-100 bg-white p-3" data-testid="highway-radial-map">
      <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-2 flex items-center gap-1">
        <Milestone size={11} /> Highway distance heat-map
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <div
          className="shrink-0"
          style={{ width: SIZE, height: SIZE }}
          dangerouslySetInnerHTML={{ __html: svgMarkup }}
        />

        <div className="flex-1 w-full min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">
            Legend
          </div>
          <ul className="space-y-1">
            <li className="flex items-center gap-2 text-xs text-slate-700">
              <span className="inline-block w-3 h-3 rounded-full bg-slate-900 border border-amber-400 shrink-0" />
              <span className="truncate">{propertyLabel}</span>
            </li>
            {items.map((h, i) => (
              <li key={`lg-${i}`} className="flex items-center gap-2 text-xs">
                <span
                  className="inline-block w-3 h-3 rounded-full shrink-0"
                  style={{ background: HW_COLORS[i % HW_COLORS.length] }}
                />
                <span className="truncate text-slate-800 font-medium">{h.name}</span>
                <span className="ml-auto text-slate-600 font-bold whitespace-nowrap">
                  {h.distance_km.toFixed(1)} km
                </span>
              </li>
            ))}
          </ul>
          <div className="text-[9px] text-slate-400 mt-1.5 leading-tight">
            Bearings are geographic (0° = N). Radius is log-scaled so short and long spokes stay
            readable together.
          </div>
        </div>
      </div>
    </div>
  );
};

export default HighwayRadialMap;
